import { FilterBarProps, MainSearchField } from "./MetadataAuditComparer.types";
import { MultiSelect } from "primereact/multiselect";
import { Tooltip } from "primereact/tooltip";
import { SelectButton } from "primereact/selectbutton";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Chip } from "primereact/chip";
import { useState, useRef } from "react";
import { MultiFieldInputProps } from "./MetadataAuditComparer.types";

export const triStateOptions = [
  { label: "All", value: null },
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export const MAIN_SEARCH_FIELDS: Array<{
  field: MainSearchField;
  placeholder: string;
}> = [
  { field: "Brand", placeholder: "Brand" },
  { field: "CustomId", placeholder: "Custom ID" },
  { field: "MCTitle", placeholder: "MC Title" },
  { field: "CompTitle", placeholder: "Comp Title" },
  { field: "SRTitle", placeholder: "SR Title" },
  { field: "MCISWC", placeholder: "MC ISWC" },
  { field: "CompISWC", placeholder: "Comp ISWC" },
  { field: "MCWriters", placeholder: "MC Writers" },
  { field: "CompWriter", placeholder: "Comp Writers" },
  { field: "SRISRC", placeholder: "SR ISRC" },
  { field: "SRArtist", placeholder: "SR Artist" },
  { field: "SRAssetId", placeholder: "SR Asset ID" },
  { field: "CompAssetId", placeholder: "Comp Asset ID" },
  { field: "UMPGInternalWorkCode", placeholder: "UMPG Internal Work Code" },
  { field: "IdentifiedIssues", placeholder: "Identified Issues" },
];

export const MultiFieldInput = ({
  field,
  placeholder,
  chips,
  inputValue,
  handleMultiDraftChange,
  handleMultiAdd,
  handleMultiRemove,
  handleMultiKeyDown,
  handleMultiClear,
}: MultiFieldInputProps) => {
  const panelRef = useRef<OverlayPanel>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleDropdownToggle = (event: React.MouseEvent<HTMLElement>) => {
    panelRef.current?.toggle(event);
    setIsPanelOpen((prev) => !prev);
  };

  return (
    <div className="comparer-chip-filter">
      <div className="comparer-chip-compact">
        <InputText
          value={inputValue}
          onChange={(e) => handleMultiDraftChange(field, e.target.value)}
          onKeyDown={(event) => handleMultiKeyDown(field, event)}
          onBlur={() => handleMultiAdd(field)}
          placeholder={placeholder}
        />
        <div className="comparer-chip-actions">
          <Button
            icon="pi pi-plus"
            className="p-button-text"
            onClick={() => handleMultiAdd(field)}
            type="button"
          />
          <Button
            icon="pi pi-bars"
            className="p-button-text"
            onClick={handleDropdownToggle}
            type="button"
          />
          <Button
            icon="pi pi-replay"
            className="p-button-text p-button-danger"
            onClick={() => handleMultiClear(field)}
            type="button"
          />
        </div>
      </div>
      <OverlayPanel
        ref={panelRef}
        onHide={() => setIsPanelOpen(false)}
        className="comparer-chip-overlay"
        appendTo={typeof window !== "undefined" ? document.body : undefined}
      >
        {chips.length > 0 ? (
          <div className="comparer-chip-list">
            {chips.map((chipValue: string) => (
              <Chip
                key={chipValue}
                label={chipValue}
                removable
                onRemove={() => {
                  handleMultiRemove(field, chipValue);
                  return true;
                }}
                className="comparer-chip-item"
              />
            ))}
          </div>
        ) : (
          <div className="comparer-chip-empty">No filters selected.</div>
        )}
      </OverlayPanel>
      {chips.length > 0 && !isPanelOpen ? (
        <div className="comparer-chip-summary">{chips.length} selected</div>
      ) : null}
    </div>
  );
};

export const FilterBar = ({
  queryFilters,
  setQueryFilters,
  visibleColumns,
  setVisibleColumns,
  discrepancyViewOptions,
  columnDefs,
  filteredExport,
  tableFilters,
  handleFieldFilter,
  tokenFilterOptionsFields,
  updateMatchConfidenceRange,
  multiDrafts,
  handleMultiDraftChange,
  handleMultiAdd,
  handleMultiRemove,
  handleMultiKeyDown,
  handleMultiClear,
  handleResetFilters,
}: FilterBarProps) => {
  return (
    <div className="comparer-filterbar">
      <div className="comparer-filter-section">
        <div className="comparer-filter-title">Main Searchable Fields</div>
        <div className="comparer-grid-text">
          {MAIN_SEARCH_FIELDS.map(({ field, placeholder }) => (
            <MultiFieldInput
              key={field}
              field={field}
              placeholder={placeholder}
              chips={
                Array.isArray(tableFilters[field]?.value)
                  ? tableFilters[field].value
                  : []
              }
              inputValue={multiDrafts[field]}
              handleMultiDraftChange={handleMultiDraftChange}
              handleMultiAdd={handleMultiAdd}
              handleMultiRemove={handleMultiRemove}
              handleMultiKeyDown={handleMultiKeyDown}
              handleMultiClear={handleMultiClear}
            />
          ))}
        </div>
      </div>

      <div className="comparer-filter-section">
        <div className="comparer-filter-title">Classification Types</div>
        <div className="comparer-grid-meta">
          <div className="comparer-filter-item">
            <MultiSelect
              value={tableFilters.DiscrepancyTypes?.value ?? []}
              options={tokenFilterOptionsFields.DiscrepancyTypes}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                handleFieldFilter("DiscrepancyTypes", e.value, "tokenAny")
              }
              placeholder="Discrepancy Types"
              display="chip"
              maxSelectedLabels={1}
              filter
            />
          </div>
          <div className="comparer-filter-item">
            <MultiSelect
              value={tableFilters.HygieneTypes?.value ?? []}
              options={tokenFilterOptionsFields.HygieneTypes}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                handleFieldFilter("HygieneTypes", e.value, "tokenAny")
              }
              placeholder="Hygiene Types"
              display="chip"
              maxSelectedLabels={1}
              filter
            />
          </div>
          <div className="comparer-filter-item">
            <MultiSelect
              value={tableFilters.DiscrepancySeverity?.value ?? []}
              options={tokenFilterOptionsFields.DiscrepancySeverity}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                handleFieldFilter("DiscrepancySeverity", e.value, "tokenAny")
              }
              placeholder="Severity"
              display="chip"
              maxSelectedLabels={1}
              filter
            />
          </div>
          <div className="comparer-filter-item">
            <label className="comparer-filter-label data-hygiene-alert-label">
              <Tooltip
                target=".data-hygiene-alert-label"
                content="Flags metadata hygiene issues (spacing, punctuation, diacritics, abbreviations). Yes = issue detected."
                position="top"
              />
              Data Hygiene Alert
            </label>
            <SelectButton
              value={tableFilters.DataHygieneAlert?.value ?? null}
              options={triStateOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                handleFieldFilter("DataHygieneAlert", e.value, "equals")
              }
            />
          </div>
          <div className="comparer-filter-item">
            <label className="comparer-filter-label manual-review-label">
              <Tooltip
                target=".manual-review-label"
                content="Rows that are marked as needing manual review."
                position="top"
              />
              Manual Review
            </label>
            <SelectButton
              value={tableFilters.ManualReviewRequired?.value ?? null}
              options={triStateOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                handleFieldFilter("ManualReviewRequired", e.value, "equals")
              }
            />
          </div>
          <div className="comparer-filter-item">
            <label className="comparer-filter-label match-confidence-label">
              <Tooltip
                target=".match-confidence-label"
                content="Confidence score (0-1) (decimals allowed); higher = stronger match, lower usually means discrepancies/manual review risk."
                position="top"
              />
              Match Confidence (Min - Max)
            </label>
            <div className="confidence-range">
              <InputNumber
                value={queryFilters.minMatchConfidence ?? 0}
                onValueChange={(e) =>
                  updateMatchConfidenceRange("minMatchConfidence", e.value ?? 0)
                }
                min={0}
                max={1}
                step={0.01}
                placeholder="Min"
                inputStyle={{ width: "5.5rem" }}
              />
              <span className="confidence-range-separator">-</span>
              <InputNumber
                value={queryFilters.maxMatchConfidence ?? 1}
                onValueChange={(e) =>
                  updateMatchConfidenceRange("maxMatchConfidence", e.value ?? 1)
                }
                min={0}
                max={1}
                step={0.01}
                placeholder="Max"
                inputStyle={{ width: "5.5rem" }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="comparer-filter-section">
        <div className="comparer-filter-title">Utility Controls</div>
        <div className="comparer-actions-row">
          <div className="comparer-filter-item">
            <SelectButton
              value={queryFilters.hasDiscrepancy ?? null}
              options={discrepancyViewOptions}
              onChange={(e) =>
                setQueryFilters((prev) => ({
                  ...prev,
                  hasDiscrepancy: e.value as boolean | null,
                }))
              }
            />
          </div>
          <div className="comparer-filter-item">
            <label className="comparer-filter-label">Show / Hide Columns</label>
            <MultiSelect
              value={visibleColumns}
              onChange={(e) => setVisibleColumns(e.value)}
              options={columnDefs}
              optionLabel="header"
              optionValue="field"
              placeholder="Show / Hide Columns"
              display="chip"
              className="w-20rem md:w-20rem"
              maxSelectedLabels={2}
              filter
            />
          </div>
          <div className="comparer-filter-item">
            <Button
              icon="pi pi-refresh"
              rounded
              label="Reset"
              severity="danger"
              onClick={handleResetFilters}
            />
          </div>
          <div className="comparer-filter-item">
            <Button
              icon="pi pi-file"
              rounded
              label="Export"
              onClick={filteredExport}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
